import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  LifeBuoy, Search, LockOpen, LogOut, KeyRound, ShieldAlert, ShieldCheck, Mail, Church,
} from 'lucide-react';
import { toast } from 'sonner';
import { Banner, Loading, fmtDate, KV } from '@/components/OpsUI';

export default function AppSupportPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canRemediate = hasRole(user, ['admin', 'tech']);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [acting, setActing] = useState(false);

  const search = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setSearching(true); setError(null);
    try {
      const { data } = await api.get('/ops/users', { params: { q: q.trim(), limit: 25 } });
      setResults(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error');
      setResults(null);
    } finally { setSearching(false); }
  };

  const openDetail = async (id) => {
    setLoadingDetail(true); setDetail({ __id: id });
    try {
      const { data } = await api.get(`/ops/users/${id}`);
      setDetail({ ...data, __id: id });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
      setDetail(null);
    } finally { setLoadingDetail(false); }
  };

  const remediate = async (action, label) => {
    if (!detail?.__id) return;
    setActing(true);
    try {
      const { data } = await api.post(`/ops/users/${detail.__id}/${action}`);
      toast.success(`${label}: ${JSON.stringify(data)}`);
      openDetail(detail.__id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setActing(false); }
  };

  const acct = detail?.account;
  const locked = detail?.lockout?.is_locked;

  return (
    <div className="space-y-6" data-testid="app-support-page">
      <div>
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <LifeBuoy className="h-6 w-6 text-[hsl(var(--primary))]" /> {t('nav_app_support')}
        </h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Tu Iglesia App · soporte y remediación</p>
      </div>

      <form onSubmit={search} className="flex gap-2 max-w-xl">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('ops_search_user')}
          data-testid="ops-support-search-input"
        />
        <Button type="submit" disabled={searching} data-testid="ops-support-search-btn">
          <Search className="h-4 w-4 mr-1" /> {t('search')}
        </Button>
      </form>

      {error && <Banner kind="error" message={String(error)} />}

      {searching ? <Loading label={t('loading')} /> : results && (
        <Card className="bg-card border border-border">
          <div className="p-4 border-b border-border text-sm text-[hsl(var(--muted-foreground))] font-mono">
            {results.count} resultado(s)
          </div>
          <div className="max-h-[480px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                  <th className="px-4 py-3">{t('ops_account')}</th><th className="px-4 py-3">{t('email')}</th><th className="px-4 py-3">role</th><th className="px-4 py-3">status</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {(results.items || []).map((u) => (
                  <tr key={u.id} className="border-b border-border/40 hover:bg-accent/40" data-testid={`ops-user-row-${u.id}`}>
                    <td className="px-4 py-3">{[u.firstname, u.lastname].filter(Boolean).join(' ') || u.handle || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">{u.role}</td>
                    <td className="px-4 py-3">{u.approval_status}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="outline" size="sm" onClick={() => openDetail(u.id)} data-testid={`ops-user-view-${u.id}`}>{t('details')}</Button>
                    </td>
                  </tr>
                ))}
                {(results.items || []).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">{t('no_data')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto">
          <SheetHeader><SheetTitle>{t('ops_user_sheet')}</SheetTitle></SheetHeader>
          {loadingDetail ? <Loading label={t('loading')} /> : acct && (
            <div className="mt-4 space-y-5" data-testid="ops-user-detail">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-base font-display font-semibold">{[acct.firstname, acct.lastname].filter(Boolean).join(' ') || acct.handle}</span>
                  {locked ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-red-300"><ShieldAlert className="h-3.5 w-3.5" /> {t('ops_locked')}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300"><ShieldCheck className="h-3.5 w-3.5" /> {t('ops_not_locked')}</span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-sm text-[hsl(var(--muted-foreground))]">
                  <Mail className="h-3.5 w-3.5" /> <span className="font-mono">{acct.email}</span>
                </div>
                {acct.email && acct.email.includes('***') && (
                  <div className="text-[11px] text-amber-300 mt-1">{t('ops_masked_hint')}</div>
                )}
              </div>

              {/* Remediación */}
              {canRemediate && (
                <Card className="p-4 bg-[hsl(var(--muted))] border border-border">
                  <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3">{t('ops_remediation')}</div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" disabled={acting} onClick={() => remediate('unlock', t('ops_unlock'))} data-testid="ops-remediate-unlock">
                      <LockOpen className="h-3.5 w-3.5 mr-1" /> {t('ops_unlock')}
                    </Button>
                    <Button variant="outline" size="sm" disabled={acting} onClick={() => remediate('revoke-sessions', t('ops_revoke'))} data-testid="ops-remediate-revoke">
                      <LogOut className="h-3.5 w-3.5 mr-1" /> {t('ops_revoke')}
                    </Button>
                    <Button variant="outline" size="sm" disabled={acting} onClick={() => remediate('password-reset', t('ops_pwd_reset'))} data-testid="ops-remediate-pwd">
                      <KeyRound className="h-3.5 w-3.5 mr-1" /> {t('ops_pwd_reset')}
                    </Button>
                  </div>
                </Card>
              )}

              <div>
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">{t('ops_account')}</div>
                <KV k="ID" v={acct.id} />
                <KV k="Handle" v={acct.handle} />
                <KV k="Role" v={acct.role} />
                <KV k="Approval" v={acct.approval_status} />
                <KV k="Phone" v={acct.phone} />
                <KV k="Visibility" v={acct.profile_visibility} />
                <KV k="Creado" v={fmtDate(acct.created_at)} />
              </div>

              {detail.church && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">{t('ops_church')}</div>
                  <KV k="Nombre" v={detail.church.name} />
                  <KV k="Slug" v={detail.church.slug} />
                  {canRemediate && detail.church.id && (
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate(`/app-churches?id=${detail.church.id}`)} data-testid="ops-view-church-btn">
                      <Church className="h-3.5 w-3.5 mr-1" /> {t('ops_view_church')}
                    </Button>
                  )}
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">{t('ops_lockout_state')}</div>
                <KV k="Fallos recientes" v={detail.lockout?.recent_failures} />
                <KV k="Ventana (min)" v={detail.lockout?.window_minutes} />
                <KV k="Bloqueado" v={detail.lockout?.is_locked ? t('yes') : t('no')} />
                <KV k="Sesiones activas" v={detail.sessions?.active} />
                <KV k="Última sesión" v={fmtDate(detail.sessions?.last_issued_at)} />
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">{t('ops_recent_contributions')}</div>
                {(detail.recent_contributions || []).length === 0 ? <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('no_data')}</div> :
                  (detail.recent_contributions).map((c) => (
                    <div key={c.id} className="flex justify-between text-xs py-1 border-b border-border/40">
                      <span className="font-mono">{c.amount_ves} VES · {c.category}</span>
                      <span className="text-[hsl(var(--muted-foreground))]">{c.status}</span>
                    </div>
                  ))}
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">{t('ops_login_attempts')}</div>
                {(detail.recent_login_attempts || []).length === 0 ? <div className="text-xs text-[hsl(var(--muted-foreground))]">{t('no_data')}</div> :
                  (detail.recent_login_attempts).map((a, i) => (
                    <div key={i} className="flex justify-between text-xs py-1 border-b border-border/40">
                      <span className={a.success ? 'text-emerald-300' : 'text-red-300'}>{a.success ? 'ok' : (a.reason || 'fail')}</span>
                      <span className="font-mono text-[hsl(var(--muted-foreground))]">{a.ip} · {fmtDate(a.created_at)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
